/**
 * Bal oldali brány vizualizáció: alap kép (Prelis M vagy L) + overlay (Povrch + Szín).
 * Base: /configurator/base-m.png vagy .jpg (fallback ha .png hibás).
 * Overlays: /configurator/overlays/{surface}_{colorCode}.jpg
 */

import { useMemo, useState, useEffect } from "react";

export function normalizeForPath(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
}

/** For overlay filename: color code without spaces (e.g. "CH 7016" → "ch7016"). */
export function normalizeColorCodeForPath(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9_-]/g, "");
}

export interface ConfiguratorPreviewProps {
  motiv: string;
  surface: string;
  colorCode: string;
  /** If set, use this filename in overlays/ instead of generated surface_colorCode.jpg. */
  overlayFilename?: string | null;
  /** Base URL for images (e.g. "" or "/" for same origin). */
  baseUrl?: string;
  className?: string;
  /** Ha megadva, a kapu ebbe a magasságba fér bele (pl. sticky mobilnézet) – így mindig teljes egészében látszik. */
  containInHeight?: number;
}

export default function ConfiguratorPreview({
  motiv,
  surface,
  colorCode,
  overlayFilename = null,
  baseUrl = "",
  className = "",
  containInHeight,
}: ConfiguratorPreviewProps) {
  const m = motiv?.toLowerCase() === "l" ? "l" : "m";
  const basePng = useMemo(() => `${baseUrl}/configurator/base-${m}.png`, [m, baseUrl]);
  const baseJpg = useMemo(() => `${baseUrl}/configurator/base-${m}.jpg`, [m, baseUrl]);

  const [baseError, setBaseError] = useState(false);
  const baseSrc = baseError ? baseJpg : basePng;
  useEffect(() => {
    setBaseError(false);
  }, [basePng]);

  const overlaySrc = useMemo(() => {
    if (overlayFilename && !/[/\\]/.test(overlayFilename)) {
      return `${baseUrl}/configurator/overlays/${overlayFilename}`;
    }
    if (!surface?.trim() || !colorCode?.trim()) return null;
    const s = normalizeForPath(surface);
    const c = normalizeColorCodeForPath(colorCode);
    if (!s || !c) return null;
    return `${baseUrl}/configurator/overlays/${s}_${c}.jpg`;
  }, [surface, colorCode, baseUrl, overlayFilename]);

  const [overlayError, setOverlayError] = useState(false);
  useEffect(() => {
    setOverlayError(false);
  }, [overlaySrc]);
  const showOverlay = overlaySrc && !overlayError;

  const innerMinH = containInHeight != null ? "min-h-0" : "min-h-[280px]";
  const aspectMaxH = containInHeight != null ? "max-h-full" : "max-h-[600px]";

  return (
    <div
      className={`relative w-full flex items-center justify-center ${className}`}
      style={containInHeight != null ? { minHeight: 0, maxHeight: "100%", height: "100%" } : { minHeight: 280 }}
      aria-hidden
    >
      <div className={`relative w-full h-full flex items-center justify-center p-4 ${innerMinH}`}>
        <div className={`relative w-full max-w-full aspect-[4/3] ${aspectMaxH}`}>
          {showOverlay && (
            <img
              src={overlaySrc}
              alt=""
              className="absolute inset-0 w-full h-full object-contain pointer-events-none z-0"
              style={{ transform: "scale(0.997)" }}
              onError={() => setOverlayError(true)}
            />
          )}
          <img
            src={baseSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-contain z-10"
            onError={() => setBaseError(true)}
          />
        </div>
      </div>
    </div>
  );
}
